import { type NineNumber } from '~/utils'

export type TabulationDataItem = {
  nineNumber: NineNumber
  count: number
  ratio: number
}

type CompareRiskTestResult = {
  brandId: number
  value: number
}

type CompareRiskOptionResult = {
  option: number
  test: CompareRiskTestResult[]
}

type PodScoreOptionResult = {
  option: number
  score: number
}

type RiskPotentialResult = {
  compare: CompareRiskOptionResult[]
  pod: PodScoreOptionResult[]
}

export type CountWhoAndWhatResult = {
  distribution: SegmentCount[]
  perception: WhoAndWhatQuestionResult[]
  keyPoint: WhoAndWhatQuestionResult[]
  riskPotential?: RiskPotentialResult | null
}

type SegmentCount = {
  segmentNumber: number
  count: number
}

type WhoAndWhatQuestionResult = {
  questionId: string
  answers: WhoAndWhatAnswerDistribution[]
}

type WhoAndWhatAnswerDistribution = {
  answerOption: {
    option: number
    text: string
  }
  distribution: WhoAndWhatDistributionItem[]
}

type WhoAndWhatDistributionItem = {
  segmentNumber: number
  count: number
}

/**
 * API レスポンスの who/what 分析結果を 9 セグメント分の TabulationDataItem[] に変換する。
 *
 * distribution はエンジンが weight_back を適用したブランドの 9 セグメント分布
 * （segmentNumber 1〜9、count は Float）を表し、ratio は合計に対する割合 (0〜1)。
 * result が null または undefined の場合は、各値が NaN の空データを返す。
 *
 * @param result API から返却される who/what 分析結果
 * @returns 9 セグメント分に正規化された TabulationDataItem[]
 */
export const transformWhoAndWhatResult = (
  result: CountWhoAndWhatResult | null | undefined,
): TabulationDataItem[] => {
  if (!result) {
    return createEmptyTabulationData()
  }

  const countBySegment = new Map<number, number>()
  for (const item of result.distribution) {
    countBySegment.set(item.segmentNumber, item.count)
  }

  let totalCount = 0
  for (const count of countBySegment.values()) {
    totalCount += count
  }

  const items: TabulationDataItem[] = []
  for (let nineNumber = 1; nineNumber <= 9; nineNumber += 1) {
    const count = countBySegment.get(nineNumber) ?? 0
    const ratio = totalCount > 0 ? count / totalCount : 0

    items.push({
      nineNumber: nineNumber as NineNumber,
      count,
      ratio,
    })
  }

  return items
}

/**
 * 空の TabulationDataItem[] を生成
 * (デフォルトはすべて NaN)
 *
 * @returns TabulationDataItem[]
 */
export const createEmptyTabulationData = (): TabulationDataItem[] => {
  const nineNumbers: NineNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9]

  return nineNumbers.map((nineNumber) => ({
    nineNumber,
    count: NaN,
    ratio: NaN,
  }))
}
